import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import PasswordStrengthBar from 'react-password-strength-bar'
import * as yup from 'yup'
import {
  Box,
  Button,
  Checkbox,
  Fab,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff, CloseOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

const UserCreateModal = ({
  open,
  handleClose,
  handleSubmit: handleCreateUser,
  handleUpdateUser,
  setRoles,
  roleNames,
  edit = false,
  activeUser,
}) => {
  const { t } = useTranslation(['common', 'messages'])

  const editSchema = yup.object().shape({
    roles: yup.array().min(1, t('messages:selectOneRole')),
  })

  const schema = yup.object().shape({
    email: yup
      .string()
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,30}$/, `${t('common:invalid')} email`)
      .required(t('common:required')),
    password: yup
      .string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!#$%&'()*+,-.])[A-Za-z\d!#$%&'()*+,-.]{8,}$/,
        t('messages:passwordInfo'),
      )
      .required(t('common:required')),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], t('messages:passwordDontMatch'))
      .required(t('common:required')),
    roles: yup.array().min(1, t('messages:selectOneRole')),
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(edit ? editSchema : schema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      roles: [],
    },
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const onSubmit = (data) => {
    if (edit) {
      handleUpdateUser(data)
    } else {
      handleCreateUser(data)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset()
        handleClose()
      }}
    >
      <Box className="modal user-admin-modal">
        <Box className="modal-top">
          <Box className="modal-titles">
            <Typography variant="title" component="h2">
              {edit ? `${t('common:edit')} ${t('common:user')}` : `${t('common:create')} ${t('common:user')}`}
            </Typography>
          </Box>
          <Fab
            variant="close"
            onClick={() => {
              reset()
              handleClose()
            }}
          >
            <CloseOutlined />
          </Fab>
        </Box>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box className="user-admin-column component-title-wrapper">
            {edit ? (
              <Box className="modal-box">
                <InputLabel>{t('common:email')}</InputLabel>
                <Typography variant="title3">{activeUser?.email}</Typography>
              </Box>
            ) : (
              <Box className="modal-box">
                <InputLabel htmlFor="email">{t('common:email')}</InputLabel>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      placeholder={`${t('common:insert')} ${t('common:email')}`}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      fullWidth
                    />
                  )}
                />
              </Box>
            )}

            {!edit && (
              <Box className="modal-box">
                <InputLabel htmlFor="password">{t('common:password')}</InputLabel>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        size="small"
                        placeholder={`${t('common:insert')} ${t('common:password')}`}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword((prev) => !prev)}
                                onMouseDown={(event) => event.preventDefault()}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                      <PasswordStrengthBar password={field.value} />
                    </>
                  )}
                />
              </Box>
            )}

            {!edit && (
              <Box className="modal-box">
                <InputLabel htmlFor="confirmPassword">{t('messages:confirmPassword')}</InputLabel>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showConfirmPassword ? 'text' : 'password'}
                      size="small"
                      placeholder={t('messages:confirmPassword')}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            )}

            <Box className="modal-box">
              <InputLabel htmlFor="roles">{t('common:roles')}</InputLabel>
              <Controller
                name="roles"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    size="small"
                    multiple
                    displayEmpty
                    error={!!errors.roles}
                    renderValue={(selected) =>
                      selected.length === 0 ? (
                        <Typography variant="subtitle3">{`${t('common:select')} ${t('common:roles')}`}</Typography>
                      ) : (
                        selected.join(', ')
                      )
                    }
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                      field.onChange(value)
                      setRoles(value)
                    }}
                  >
                    {roleNames?.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={field.value.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.roles && (
                <Typography variant="caption" color="error">
                  {errors.roles.message}
                </Typography>
              )}
            </Box>
          </Box>

          <Button size="small" variant="contained" type="submit" sx={{ marginLeft: 'auto' }} disabled={!isValid}>
            {t('common:save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default UserCreateModal
